import React, { useEffect, useRef, useState } from 'react';
import { DragIndicator } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useDebounceValue } from 'usehooks-ts';
import { useSettings } from '@juno/utils';

interface RightExpandableDrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  bottomContent?: React.ReactNode;
  confirmText?: string;
  handleSubmit?: () => void;
  handleCancel?: () => void;
}

const RightExpandableDrawer: React.FC<RightExpandableDrawerProps> = ({
  open,
  onClose,
  children,
  bottomContent,
}) => {
  const [drawerWidth, setDrawerWidth] = useState(450);
  const isDragging = useRef(false);
  const { isSmallScreen } = useSettings();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [value] = useDebounceValue(windowWidth, 100);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  useEffect(() => {
    if (windowWidth < drawerWidth) {
      if (isSmallScreen) {
        setDrawerWidth(windowWidth);
        return;
      }
      setDrawerWidth(windowWidth - 20);
    }
  }, [value]);

  // Update the drawer width based on 0999898988ihe current breakpoint
  useEffect(() => {
    if (isSmallScreen) {
      setDrawerWidth(window.innerWidth); // Set to full screen width on mobile
    } else {
      const lastWidth = localStorage.getItem('desiredDrawerWidth');
      setDrawerWidth(lastWidth ? Math.min(parseInt(lastWidth), 450) : 450);
    }
  }, [isSmallScreen]);

  const startResize = (e: React.MouseEvent) => {
    isDragging.current = true;
    const startX = e.clientX;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (isDragging.current) {
        const currentX = moveEvent.clientX;
        const deltaX = startX - currentX;
        const minWidth = isSmallScreen ? window.innerWidth : 450; // Use full screen width on mobile
        const newWidth = Math.min(Math.max(drawerWidth + deltaX, minWidth), 1200);
        setDrawerWidth(newWidth);
        localStorage.setItem('desiredDrawerWidth', newWidth.toString());
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    // Attach global event listeners
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Ensure the drawer width is consistent when opening
  useEffect(() => {
    if (open) {
      const lastWidth = localStorage.getItem('desiredDrawerWidth');
      const initialWidth = isSmallScreen
        ? window.innerWidth
        : lastWidth
        ? parseInt(lastWidth)
        : 450;
      setDrawerWidth(initialWidth);
    }
  }, [open, isSmallScreen]);

  return (
    <Drawer
      disableEnforceFocus={true}
      variant='temporary'
      anchor='right'
      open={open}
      sx={{
        maxWidth: '100vw!important',
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          overflow: 'visible',
        },
        '& .MuiBackdrop-root': {
          opacity: '0.5 !important',
          backgroundColor: '#7f7f7f',
        },
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          py: 3,
          px: 4,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={() => onClose()}
          sx={{ color: '#000', position: 'absolute', top: 28, right: 32 }}
          size='small'
          aria-label='Close'
        >
          <CloseIcon sx={{ fontSize: 19 }} />
        </IconButton>

        {children}
      </Box>

      {/* Bottom section */}
      {bottomContent && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            p: 2,
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            bgcolor: 'background.paper',
          }}
        >
          {bottomContent}
        </Box>
      )}

      {/* Draggable handle */}
      {!isSmallScreen && (
        <Box
          onMouseDown={startResize}
          sx={{
            width: '24px',
            height: '92px',
            position: 'absolute',
            left: '-24px',
            top: 'calc(50% - 46px)',
            cursor: 'grab',
            zIndex: 2000,
            bgcolor: 'white',
            borderRadius: '12px 0 0 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack>
            <DragIndicator />
            <DragIndicator />
          </Stack>
        </Box>
      )}
    </Drawer>
  );
};

export default RightExpandableDrawer;

export const DrawerHeader = ({ title }: { title: string }) => {
  return (
    <Box sx={{ mb: '40px' }}>
      <Box display='flex' justifyContent={'space-between'} alignItems={'center'}>
        <Typography variant='h5' sx={{ mb: '20px' }}>
          {title}
        </Typography>
      </Box>
      <Divider />
    </Box>
  );
};

export const DrawerFooter = ({
  onSubmit,
  onCancel,
  confirmButtonText = 'Save',
  disabled = false,
  submitting = false,
}: {
  onSubmit: () => void;
  onCancel: () => void;
  confirmButtonText?: string;
  disabled: boolean;
  submitting: boolean;
}) => {
  return (
    <Stack direction='row' sx={{ pt: '20px' }} gap={2}>
      <LoadingButton
        disabled={disabled}
        loading={submitting}
        variant='contained'
        type='submit'
        fullWidth
        size='small'
        onClick={() => onSubmit()}
        sx={{ height: 38 }}
        aria-label='Save'
      >
        {confirmButtonText ? confirmButtonText : 'Save'}
      </LoadingButton>
      <Button
        fullWidth
        onClick={(e) => {
          e.stopPropagation();
          onCancel();
        }}
        disabled={submitting}
        variant='outlined'
        size='small'
        sx={{ height: 38 }}
        aria-label='Cancel'
      >
        Cancel
      </Button>
    </Stack>
  );
};
